<template>
  <Header :title="$t('uploadStatus.title')" />
  <div class="tenantManagementContainer">
    <div class="pageContainerJob">
      <div class="customRowTenant">
        <p class="jobHistoryTitle">{{ $t('uploadStatus.title') }}</p>
      </div>

      <div class="tableContainer">
        <table class="customTable">
          <thead class="customTableHeader">
            <tr class="customTableRow">
              <th class="customTableBodyTd">#</th>
              <th class="customTableBodyTd">{{ $t('uploadStatus.table.file_name') }}</th>
              <th class="customTableBodyTd">{{ $t('uploadStatus.table.status') }}</th>
              <th class="customTableBodyTd">{{ $t('uploadStatus.table.action') }}</th>
            </tr>
          </thead>
          <tbody class="customTableBody">
            <tr v-for="(file, index) in uploadedFiles" :key="index" :class="{ 'alternate-row': index % 2 !== 0 }">
              <td class="customTableBodyTd">{{ index + 1 }}</td>
              <td class="customTableBodyTd">{{ file.name }} / {{ formatFileSize(file.size) }}</td>
              <td class="customTableBodyTd">
                <div class="progress-container">
                  <span :class="getStatusClass(file.status)">
                    {{ $t(`uploadStatus.status.${file.status.toLowerCase().replace(' ', '_')}`) }}
                  </span>
                  <div class="progress-bar">
                    <div
                      class="progress"
                      :style="{ width: file.progress + '%' }"
                      :class="{
                        'progress-complete': file.status === 'Completed',
                      }"
                    ></div>
                  </div>
                  <span class="progress-text">{{ file.progress }}%</span>
                </div>
              </td>
              <td class="customTableBodyTd">
                <!-- {{ file.mx_job_id }} -->
                <button v-if="file.status === 'Failed'" class="re-upload-btn" @click="handleReUpload(index)">{{ $t('uploadStatus.actions.re_upload') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="button-container">
        <button class="done-button" @click="navigateToJobHistory" :disabled="uploadedFiles.some((file) => file.status === 'In Process') || isUploading">{{ $t('uploadStatus.actions.start_job') }}</button>
      </div>
    </div>
  </div>

  <ConfirmationModal :show="showLeaveConfirmModal" :title="$t('uploadStatus.leave_confirm_title')" :message="$t('uploadStatus.leave_without_starting')" @close="handleLeaveConfirmClose" @confirm="handleLeaveConfirm" />
</template>

<script>
import Header from '../../components/Header.vue'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import { post, put } from '../../utils/api.js'
import { showToast } from '@/utils/toast'

export default {
  components: {
    Header,
    ConfirmationModal,
  },
  data() {
    return {
      uploadedFiles: [],
      jobId: null,
      statusInterval: null,
      isUploading: false,
      jobStarted: false,
      showLeaveConfirmModal: false,
      pendingRouteNext: null,
    }
  },
  beforeRouteLeave(to, from, next) {
    const hasUploadedFiles = this.uploadedFiles.length > 0
    if (hasUploadedFiles && !this.jobStarted) {
      this.pendingRouteNext = next
      this.showLeaveConfirmModal = true
    } else {
      next()
    }
  },
  mounted() {
    this.loadUploadedFiles()

    this.beforeUnloadHandler = (e) => {
      if (this.uploadedFiles.length > 0 && !this.jobStarted) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)

    // Debug: List all files in IndexedDB
    this.listAllIndexedDBFiles()

    // Check if we have any files to upload
    const hasVasionFiles = this.uploadedFiles.some((f) => f.isVasion)
    const hasLocalFiles = this.uploadedFiles.some((f) => !f.isVasion && (f.status === 'Pending' || f.status === 'Failed'))
    const hasInProcessFiles = this.uploadedFiles.some((f) => f.status === 'In Process')

    if (hasVasionFiles || hasLocalFiles || hasInProcessFiles) {
      // Add a small delay to ensure IndexedDB is ready
      setTimeout(() => {
        this.startUploading()
      }, 100)
    } else if (this.uploadedFiles.length === 0) {
      // No files at all
      this.showFileSelectionMessage()
    }
    // If files exist but are all completed, don't show any message
  },

  beforeUnmount() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
    // Clear the interval when component is destroyed
    if (this.statusInterval) {
      clearInterval(this.statusInterval)
      localStorage.removeItem('classificationData')
      localStorage.removeItem('vasionUploadResult') // cleanup if present
      localStorage.removeItem('uploadStatusData')

      // Clean up IndexedDB data
      this.cleanupIndexedDB()
    }
  },
  methods: {
    handleLeaveConfirmClose() {
      if (this.pendingRouteNext) {
        this.pendingRouteNext(false)
        this.pendingRouteNext = null
      }
      this.showLeaveConfirmModal = false
    },
    handleLeaveConfirm() {
      if (this.pendingRouteNext) {
        this.pendingRouteNext()
        this.pendingRouteNext = null
      }
      this.showLeaveConfirmModal = false
    },
    loadUploadedFiles() {
      const vasionData = localStorage.getItem('vasionUploadResult')
      const localData = localStorage.getItem('uploadStatusData')

      const files = []

      if (vasionData) {
        const parsed = JSON.parse(vasionData)
        this.jobId = parsed.jobId
        files.push(
          ...parsed.uploadStatusList.map((f) => ({
            name: f.fileName,
            size: f.fileSize || 0,
            status: f.status === 'SUCCESS' ? 'Completed' : 'Failed',
            progress: 100,
            s3Path: f.s3Path,
            isVasion: true,
          }))
        )
      }

      if (localData) {
        const parsed = JSON.parse(localData)
        this.jobId = this.jobId || parsed.jobId
        files.push(...parsed.files)
      }

      this.uploadedFiles = files
    },
    showFileSelectionMessage() {
      showToast({
        title: 'Files Not Available',
        message: 'File data is not available. Please go back to New Job and re-select your files.',
        type: 'warning',
      })
    },
    async startUploading() {
      // Upload files one by one
      for (let i = 0; i < this.uploadedFiles.length; i++) {
        const file = this.uploadedFiles[i]

        if (file.status === 'Pending' || file.status === 'Failed') {
          try {
            this.updateFileStatus(i, 'In Process')
            await this.uploadFileToS3(i)
            this.updateFileStatus(i, 'Completed')
          } catch (error) {
            this.updateFileStatus(i, 'Failed')
          }
        }
      }
    },

    uploadFileToS3(fileIndex) {
      const file = this.uploadedFiles[fileIndex]

      return new Promise((resolve, reject) => {
        this.getFileFromIndexedDB(file.fileKey)
          .then((fileData) => {
            if (!fileData) {
              reject(new Error('File data not available. Please re-select files in New Job.'))
              return
            }

            // Convert base64 back to Blob
            return fetch(fileData)
          })
          .then((res) => res.blob())
          .then((blob) => {
            const xhr = new XMLHttpRequest()

            // Track upload progress
            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100)
                this.updateFileProgress(fileIndex, progress)
              }
            }

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
              } else {
                reject(new Error(`Upload failed: ${xhr.status}`))
              }
            }

            xhr.onerror = () => {
              reject(new Error('Upload failed'))
            }

            xhr.open('PUT', file.s3_path, true)
            xhr.setRequestHeader('Content-Type', file.type || 'application/pdf')

            // Send the blob
            xhr.send(blob)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    getFileFromIndexedDB(fileKey) {
      return new Promise((resolve, reject) => {
        // Check if IndexedDB is supported
        if (!window.indexedDB) {
          reject(new Error('IndexedDB is not supported in this browser'))
          return
        }

        const request = indexedDB.open('FileStorage', 1)

        request.onerror = () => {
          reject(new Error('Failed to open IndexedDB: ' + request.error.message))
        }

        request.onsuccess = () => {
          const db = request.result
          const transaction = db.transaction(['files'], 'readonly')
          const store = transaction.objectStore('files')
          const getRequest = store.get(fileKey)

          getRequest.onerror = () => {
            reject(new Error('Failed to retrieve file from IndexedDB: ' + getRequest.error.message))
          }

          getRequest.onsuccess = () => {
            const result = getRequest.result
            resolve(result ? result.data : null)
          }
        }
      })
    },

    updateFileStatus(index, status) {
      this.uploadedFiles[index].status = status
      this.saveToLocalStorage()
    },

    updateFileProgress(index, progress) {
      this.uploadedFiles[index].progress = progress
      this.saveToLocalStorage()
    },

    saveToLocalStorage() {
      const uploadData = {
        jobId: this.jobId,
        timestamp: new Date().getTime(),
        files: this.uploadedFiles,
      }
      localStorage.setItem('uploadStatusData', JSON.stringify(uploadData))
    },

    async handleReUpload(index) {
      try {
        // Reset status and progress
        this.updateFileStatus(index, 'Pending')
        this.updateFileProgress(index, 0)

        const file = this.uploadedFiles[index]

        // Get new presigned URL
        // Include existing fileId if available to help backend update the existing entry
        // instead of creating a duplicate. If backend doesn't support this, it will ignore the field.
        const requestPayload = {
          mxJobId: file.mx_job_id,
          fileNames: [file.name],
        }

        // If we have an existing fileId, include it so backend can update instead of create
        if (file.fileId) {
          requestPayload.fileId = file.fileId
        }

        const response = await post('/v1/uploads/presigned-url', requestPayload)

        if (!response?.data?.[0]?.s3_path) {
          throw new Error('Invalid presigned URL response')
        }

        const presignedData = response.data[0]

        // Update all file properties from the presigned URL response
        // This ensures we have the latest fileId and other metadata
        if (presignedData.id) {
          file.fileId = presignedData.id
        }
        file.s3_path = presignedData.s3_path
        if (presignedData.mx_job_id) {
          file.mx_job_id = presignedData.mx_job_id
        }
        if (presignedData.tenant_id) {
          file.tenant_id = presignedData.tenant_id
        }
        if (presignedData.automation !== undefined) {
          file.automation = presignedData.automation
        }

        this.saveToLocalStorage()

        // Start upload
        await this.uploadFileToS3(index)

        this.updateFileStatus(index, 'Completed')
      } catch (error) {
        this.updateFileStatus(index, 'Failed')
        showToast({
          title: 'Re-Upload Failed',
          message: error?.message || 'Failed to re-upload file. Please try again.',
          type: 'error',
        })
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(k)))
      return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i]
    },

    getStatusClass(status) {
      return {
        'status-completed': status === 'Completed',
        'status-in-process': status === 'In Process',
        'status-failed': status === 'Failed',
      }
    },

    async navigateToJobHistory() {
      try {
        this.isUploading = true
        const res = await put(`/v1/mx-jobs/${this.jobId}`, {})

        if (res && res.status === 200) {
          // Proceed only if response is OK
          this.jobStarted = true
          localStorage.removeItem('classificationData')
          localStorage.removeItem('vasionUploadResult')
          localStorage.removeItem('uploadStatusData')

          // Clean up IndexedDB data
          this.cleanupIndexedDB()

          this.handleViewJobHistory()
        } else {
          // Show toast if response is not OK
          showToast({
            title: 'Error',
            message: res?.data?.message || 'Failed to update job',
            type: 'error',
          })
        }
      } catch (error) {
        // Show toast on request failure
        showToast({
          title: 'Error',
          message: error?.response?.data?.message || 'Failed to update job',
          type: 'error',
        })
      } finally {
        this.isUploading = false
      }
    },
    handleViewJobHistory() {
      const tenantName = localStorage.getItem('url_name')
      this.$router.push(`/${tenantName}/job-history`)
    },
    cleanupIndexedDB() {
      // Check if IndexedDB is supported
      if (!window.indexedDB) {
        return
      }

      const request = indexedDB.open('FileStorage', 1)

      request.onerror = () => {}

      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readwrite')
        const store = transaction.objectStore('files')

        // Clear all files for this job
        this.uploadedFiles.forEach((file) => {
          if (file.fileKey) {
            const deleteRequest = store.delete(file.fileKey)
            deleteRequest.onerror = () => {}
          }
        })
      }
    },
    listAllIndexedDBFiles() {
      if (!window.indexedDB) {
        return
      }

      const request = indexedDB.open('FileStorage', 1)
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readonly')
        const store = transaction.objectStore('files')
        const getAllRequest = store.getAll()

        getAllRequest.onsuccess = () => {}

        getAllRequest.onerror = () => {}
      }
    },
  },
}
</script>

<style scoped>
.pageContainerJob {
  height: calc(100vh - 100px);
}

.progress-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}

.progress-bar {
  width: 150px;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background-color: #2196f3;
  transition: width 0.3s ease;
}

.progress-complete {
  background-color: #7ac4eb;
}

.progress-text {
  min-width: 45px;
  text-align: right;
}

.status-completed {
  color: #4caf50;
  font-weight: 500;
}

.status-in-process {
  color: #2196f3;
  font-weight: 500;
}

.status-failed {
  color: #f44336;
  font-weight: 500;
}

.re-upload-btn {
  width: 178px;
  height: 47px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--primary-color);
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.re-upload-btn:hover {
  background: #1a5a7a;
}

.done-button {
  width: 178px;
  height: 47px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--primary-color);
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.done-button:hover {
  background: #1a5a7a;
}

.done-button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px;
}

.tableContainer {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 70vh;
  overflow-y: auto;
}

.customTable {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
}

.customTableHeader tr {
  background: #d5e3eb;
  height: 50px;
}

.customTableHeader th {
  padding: 12px;
  font-weight: 600;
  text-align: center;
}

.customTableBody td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  height: 50px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* max-width: 200px; */
}

.alternate-row {
  background: #f5f5f5;
}

.customTableBodyTd {
  text-align: left !important;
}
</style>
